import type { CustomerBalanceReportFilters } from '@/lib/services/reports/reports.schema'

import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import { useIndexTableSearchParams } from '@/components/tables/useIndexTableSearchParams'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { usePhoneFormatter } from '@/hooks/use-phone-formatter'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useAbility } from '@/lib/casl'
import { canRunStatements } from '@/lib/services/money/money.access'
import { useAppForm } from '@/lib/form'
import { getOrganizationReportDateBounds } from '@/lib/report-date-range'
import {
  organizationQueries,
  useOrganization,
} from '@/lib/services/organizations/organizations.query'
import { prefetchIndexTableRouteQuery } from '@/lib/services/index-table-query-options'
import {
  getCustomerBalanceReportFiltersFromSearch,
  getCustomerBalanceReportSearchParamsFromSearch,
  customerBalanceReportSearchSchema,
} from '@/lib/services/reports/reports.schema'
import {
  reportQueries,
  useCustomerBalanceReport,
  useExportCustomerBalanceReportMutation,
} from '@/lib/services/reports/reports.query'
import { createFileRoute, Link, useNavigate, type RegisteredRouter } from '@tanstack/react-router'
import { Download, Send, UsersRound } from 'lucide-react'
import { startTransition } from 'react'
import { toast } from 'sonner'
import { getOrganizationNow } from '@/lib/reporting-dates'
import { downloadExportedReport, getExportErrorMessage } from '../../reports/-helpers'
import { CustomerBalanceReportResults } from './-results'

export const Route = createFileRoute('/_authenticated/$orgId/money/customer-balances/')({
  component: CustomerBalanceReportPage,
  validateSearch: (search) => customerBalanceReportSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ as_of_date: search.as_of_date }),
  loader: async ({ context, deps, params }) => {
    if (Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)) {
      return { isReadOnly: false }
    }

    const organization =
      context.queryClient.getQueryData?.(organizationQueries.show(params.orgId).queryKey) ??
      (await context.queryClient.ensureQueryData(organizationQueries.show(params.orgId)))
    const filters = getReportFilters(deps, organization?.timezone)

    void prefetchIndexTableRouteQuery(
      context.queryClient,
      reportQueries.customerBalances({
        organizationId: params.orgId,
        filters,
      })
    )

    return { isReadOnly: false }
  },
})

export function CustomerBalanceReportPage() {
  const navigate = useNavigate<RegisteredRouter, typeof Route.fullPath>()
  const path = usePathHelper()
  const ability = useAbility()
  const { formatPhone } = usePhoneFormatter()
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const { data: organization } = useOrganization(orgId)
  const reportSearch = getReportSearch(search, organization?.timezone)
  const filters = getReportFilters(search, organization?.timezone)
  const { data: report, isFetching } = useCustomerBalanceReport(filters)
  const exportMutation = useExportCustomerBalanceReportMutation()
  const organizationNow = getOrganizationNow(organization?.timezone)
  const datePickerBounds = getOrganizationReportDateBounds(
    organization?.created_at,
    organizationNow,
    organization?.timezone
  )
  const hasExportData = (report?.customers?.length ?? 0) > 0
  const { updateSearchTerm } = useIndexTableSearchParams({
    navigate,
    getSearchParams: (previous) => getReportSearch(previous, organization?.timezone),
    searchTerm: reportSearch.search ?? '',
    resetPageOnSearch: false,
  })

  const applyFilters = ({ as_of_date }: CustomerBalanceReportFilters) => {
    startTransition(() => {
      void navigate({
        ...path.customerBalances.index().using({ orgId }).link,
        search: {
          ...reportSearch,
          as_of_date,
        },
        replace: Boolean(search.as_of_date || search.search),
      })
    })
  }

  const handleExport = async () => {
    try {
      const response = await exportMutation.mutateAsync({
        organizationId: orgId,
        filters,
      })

      downloadExportedReport(response, `customer-balance-report-${filters.as_of_date}.xlsx`)
    } catch (error) {
      toast.error(await getExportErrorMessage(error, 'Failed to export customer balance report'))
    }
  }

  return (
    <div className="space-y-6 pb-6">
      <AdminPageHeader
        icon={UsersRound}
        title="Customer Balances"
        description="See unpaid customer balances by due date and past-due age."
        className="mb-4 mt-2"
        actions={
          <>
            {canRunStatements(ability) ? (
              <AdminHeaderActionButton asChild>
                <Link
                  {...path.statements.index().using({ orgId }).link}
                  search={{
                    cutoff_date: filters.as_of_date,
                    search: reportSearch.search || undefined,
                  }}
                >
                  <Send />
                  Create statements
                </Link>
              </AdminHeaderActionButton>
            ) : null}
            <CustomerBalanceDateFilter
              key={filters.as_of_date}
              asOfDate={filters.as_of_date}
              minDate={datePickerBounds.minDate}
              maxDate={datePickerBounds.maxDate}
              onChange={(as_of_date) => applyFilters({ as_of_date })}
            />
            {hasExportData ? (
              <AdminHeaderActionButton
                type="button"
                variant="outline"
                onClick={() => {
                  void handleExport()
                }}
                disabled={exportMutation.isPending}
              >
                <Download className="h-4 w-4" />
                Export Spreadsheet
              </AdminHeaderActionButton>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0}>
                    <AdminHeaderActionButton type="button" variant="outline" disabled>
                      <Download className="h-4 w-4" />
                      Export Spreadsheet
                    </AdminHeaderActionButton>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Nothing to export.</TooltipContent>
              </Tooltip>
            )}
          </>
        }
      />

      <CustomerBalanceReportResults
        report={report}
        orgId={orgId}
        path={path}
        formatPhone={formatPhone}
        searchTerm={reportSearch.search ?? ''}
        onSearchChange={updateSearchTerm}
        isFetching={isFetching}
      />
    </div>
  )
}

function CustomerBalanceDateFilter({
  asOfDate,
  minDate,
  maxDate,
  onChange,
}: {
  asOfDate: string
  minDate?: string
  maxDate?: string
  onChange: (value: string) => void
}) {
  const form = useAppForm({
    defaultValues: {
      as_of_date: asOfDate,
    },
  })

  return (
    <form.AppForm>
      <form.AppField
        name="as_of_date"
        listeners={{
          onChange: ({ value }) => {
            if (value && value !== asOfDate) {
              onChange(value)
            }
          },
        }}
      >
        {(field) => (
          <field.DatePicker
            format="ddd, MMM D, YYYY"
            placeholder="Select a date"
            minDate={minDate}
            maxDate={maxDate}
            buttonProps={{
              id: 'customer-balance-as-of-date',
              'aria-label': 'As of date',
            }}
          />
        )}
      </form.AppField>
    </form.AppForm>
  )
}

function getReportFilters(
  search: unknown,
  organizationTimezone: Parameters<typeof getOrganizationNow>[0]
): CustomerBalanceReportFilters {
  const defaultFilters = getReportDefaultFilters(organizationTimezone)

  return getCustomerBalanceReportFiltersFromSearch(search, defaultFilters)
}

function getReportSearch(
  search: unknown,
  organizationTimezone: Parameters<typeof getOrganizationNow>[0]
) {
  return getCustomerBalanceReportSearchParamsFromSearch(
    search,
    getReportDefaultFilters(organizationTimezone)
  )
}

function getReportDefaultFilters(
  organizationTimezone: Parameters<typeof getOrganizationNow>[0]
): CustomerBalanceReportFilters {
  return {
    as_of_date: getOrganizationNow(organizationTimezone).format('YYYY-MM-DD'),
  }
}
